import { useEffect, useState, type FormEvent } from "react";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { FormField } from "@/components/ui/FormField";
import { Input } from "@/components/ui/Input";
import { ApiError } from "@/lib/api";

import { useAccountProfile, useUpdateProfile } from "./api";

type Channel = "email" | "sms";

// Phase 2a customer-accounts FR-7 — name, phone, company, preferred
// notification channel. Email change lives on the Security tab because
// it requires the current password (FR-15).
export function ProfileTab(): JSX.Element {
  const profile = useAccountProfile();
  const update = useUpdateProfile();

  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [channel, setChannel] = useState<Channel>("email");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  // Hydrate the form once the API responds.
  useEffect(() => {
    if (!profile.data) return;
    setFirst(profile.data.first_name);
    setLast(profile.data.last_name);
    setPhone(profile.data.phone);
    setCompany(profile.data.company_name ?? "");
    setChannel(profile.data.preferred_notification_channel);
  }, [profile.data]);

  // Auto-dismiss the success banner after 4s so subsequent saves can
  // re-trigger it (single-flash UX, not sticky).
  useEffect(() => {
    if (!saved) return;
    const t = window.setTimeout(() => setSaved(false), 4000);
    return () => window.clearTimeout(t);
  }, [saved]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});
    setSaved(false);
    try {
      await update.mutateAsync({
        first_name: first,
        last_name: last,
        phone,
        company_name: company || null,
        preferred_notification_channel: channel
      });
      setSaved(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        const body = err.body as { errors?: Record<string, string> } | undefined;
        setErrors(body?.errors ?? {});
      }
    }
  };

  if (profile.isLoading) {
    return (
      <p className="text-sm text-zinc-500" role="status">
        Loading your profile…
      </p>
    );
  }
  if (profile.isError || !profile.data) {
    return (
      <Alert variant="destructive" role="alert">
        Couldn&rsquo;t load your profile. Refresh the page.
      </Alert>
    );
  }

  return (
    <form onSubmit={onSubmit} className="max-w-lg space-y-4">
      {saved ? (
        <Alert variant="success" role="status">
          Profile saved.
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <FormField id="first_name" label="First name" required error={errors.first_name}>
          <Input id="first_name" value={first} onChange={(e) => setFirst(e.target.value)} autoComplete="given-name" required />
        </FormField>
        <FormField id="last_name" label="Last name" required error={errors.last_name}>
          <Input id="last_name" value={last} onChange={(e) => setLast(e.target.value)} autoComplete="family-name" required />
        </FormField>
      </div>

      <FormField id="phone" label="Phone" required hint="Include country code, e.g. +1 720-555-1234" error={errors.phone}>
        <Input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" required />
      </FormField>

      <FormField id="company_name" label="Company (optional)" error={errors.company_name}>
        <Input id="company_name" value={company} onChange={(e) => setCompany(e.target.value)} autoComplete="organization" />
      </FormField>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-brand-black">Preferred notifications</legend>
        <label className="flex items-center gap-2 text-sm text-zinc-700">
          <input
            type="radio"
            name="notification-channel"
            value="email"
            checked={channel === "email"}
            onChange={() => setChannel("email")}
          />
          Email
        </label>
        <label className="flex items-center gap-2 text-sm text-zinc-500">
          <input
            type="radio"
            name="notification-channel"
            value="sms"
            checked={channel === "sms"}
            onChange={() => setChannel("sms")}
            disabled
          />
          SMS <span className="text-xs">(coming soon)</span>
        </label>
      </fieldset>

      <Button type="submit" disabled={update.isPending}>
        {update.isPending ? "Saving…" : "Save profile"}
      </Button>
    </form>
  );
}
